import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";
import { getSampleByContext, sampleRoaches } from "./data/sampleRoachData";
import DriftIntelligence from './modules/pulse/DriftIntelligence';
import CampusPulse from './modules/pulse/CampusPulse';
import PulseStreakCalendar from './modules/pulse/PulseStreakCalendar';
import PulseEvolutionScreen from './modules/pulse/PulseEvolutionScreen';
import AILightComponent from "../../components/AILightComponent";

const Bar = ({ value = 0, color = "bg-cyan-400" }) => {
    return (
        <div className="w-full h-3 bg-gray-800 rounded-full overflow-hidden">
            <div
                className={`h-full rounded-full ${color}`}
                style={{ width: `${value}%` }}
            />
        </div>
    );
};

const PDRoach = ({ useSample, isActiveSub, subscriptionId, startDate, endDate }) => {
    const authState = useSelector((state) => state.authReducer);
    const [fetchData] = useFetch();

    const userId = authState?.user?._id;
    const userCode = authState?.user?.think5whyscode;

    // ─── UI State ──────────────────────────────────────────────────────────────
    const [screen, setScreen] = useState("today");
    const [showHistory, setShowHistory] = useState(false);
    const [selectedRoachId, setSelectedRoachId] = useState(null);
    const [expandedHints, setExpandedHints] = useState({});
    const [openHint, setOpenHint] = useState(null);
    const [message, setMessage] = useState({ text: "", type: "" });
    const [successMsg, setSuccessMsg] = useState("");

    // const [enableGenAIHelp, setEnableGenAIHelp] = useState(false);
    // const toggleGenAIHelp = () => setEnableGenAIHelp((p) => !p);
    const [openGenAI, setOpenGenAI] = useState(null);
    const toggleGenAIHelp = (index) => {
        setOpenGenAI(openGenAI === index ? null : index);
    };

    // ─── Data State ────────────────────────────────────────────────────────────
    const [currentDay, setCurrentDay] = useState(17);
    const [streak, setStreak] = useState(6);
    const [history, setHistory] = useState([]);

    // BUG 1 FIX: answers must be an object {} not an array []
    // selectAnswer uses object keys, generateProfile uses Object.values()
    const [answers, setAnswers] = useState({});
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [progressLoading, setProgressLoading] = useState(true);

    // ─── Date & Context State ──────────────────────────────────────────────────
    const [pulseContext, setPulseContext] = useState("Overall");

    const getTodayLocal = () => {
        const d = new Date();
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        return d.toISOString().split("T")[0];
    };

    const [roachDate, setRoachDate] = useState(getTodayLocal());

    // ─── Refs ──────────────────────────────────────────────────────────────────
    // BUG 3 FIX: ref tracks whether initial fetch has completed (not just started)
    const hasFetchedRoachRef = useRef(false);

    // ─── Derived Values ────────────────────────────────────────────────────────
    // const displayRoaches = useSample ? sampleRoaches : history;
    const displayRoaches = useSample
        ? getSampleByContext(pulseContext)        // sample: filtered to selected tab
        : history.filter((r) => r.pulseContext === pulseContext);  // live: same filter

    const isSubscriptionActive =
        isActiveSub && new Date(endDate) >= new Date(roachDate || new Date());

    const maxAllowedDate =
        new Date(endDate) < new Date()
            ? endDate.split("T")[0]
            : getTodayLocal();

    const normalizeDate = (date) =>
        new Date(date).toISOString().split("T")[0];

    // BUG 2 FIX: was using `selectedSubscriptionId` (undefined). Use `subscriptionId` prop.
    const isDuplicateEntry =
        !!roachDate &&
        history.some(
            (roach) =>
                normalizeDate(roach.roachDate) === normalizeDate(roachDate) &&
                roach.pulseContext === pulseContext &&
                roach.subscriptionId === subscriptionId
        );

    const isSaveDisabled =
        loading || !result || !roachDate || isDuplicateEntry || !isSubscriptionActive;

    // ─── Static Data ───────────────────────────────────────────────────────────
    const roachquestions = [
        "🌪 Chaos Level?",
        "🔥 Heat Exposure?",
        "🎭 Humor Shield?",
        "🧊 Numb Mode?",
        "⚡ Focus Spark?",
        "📱 Doom Scroll Damage?",
        "🧠 Mental Recovery Speed?",
    ];

    const roachHints = [
        {
            meaning: "Measures how chaotic your environment, emotions, and schedule feel today.",
            impact: "Too much chaos reduces focus, sleep quality, and consistency.",
            improve: "Reduce noise, simplify tasks, and focus on one priority block.",
        },
        {
            meaning: "Measures stress, pressure, workload, and emotional intensity.",
            impact: "High heat for too long leads to burnout and poor decisions.",
            improve: "Take breaks, hydrate, sleep better, and avoid overload cycles.",
        },
        {
            meaning: "Measures your ability to survive stress with humor and perspective.",
            impact: "People with humor resilience recover faster from setbacks.",
            improve: "Avoid overthinking. Learn to detach and reset emotionally.",
        },
        {
            meaning: "Measures emotional shutdown and low-energy survival mode.",
            impact: "Long numb phases reduce growth, confidence, and motivation.",
            improve: "Move physically, talk to people, and restart small wins.",
        },
        {
            meaning: "Measures your mental sharpness and ability to focus deeply.",
            impact: "Focus directly affects learning, work quality, and discipline.",
            improve: "Use deep work sessions before social media or distractions.",
        },
        {
            meaning: "Measures distraction addiction and random scrolling damage.",
            impact: "Excessive scrolling destroys attention span and consistency.",
            improve: "Use app timers and remove instant dopamine triggers.",
        },
        {
            meaning: "Measures how quickly you mentally recover after failure or stress.",
            impact: "Fast recovery builds resilience and long-term survival ability.",
            improve: "Reflect, reset quickly, and avoid emotional spirals.",
        },
    ];

    const pulseContexts = [
        { id: "Overall", emoji: "🌍", label: "Overall" },
        { id: "Home", emoji: "🏠", label: "Home" },
        { id: "Work", emoji: "💼", label: "Work" },
        { id: "Campus", emoji: "🏫", label: "CampusPulse" },
        { id: "Learning", emoji: "🎓", label: "Learning" },
        { id: "Social", emoji: "🌐", label: "Social" },
        { id: "Environment", emoji: "🌆", label: "Environment" },
    ];

    const selectedPulseLabel =
        pulseContexts.find((c) => c.id === pulseContext)?.label || pulseContext;
    const selectedPulseEmoji =
        pulseContexts.find((c) => c.id === pulseContext)?.emoji || "🪳";

    // ─── Helpers ───────────────────────────────────────────────────────────────
    const showMessage = (text, type = "success") => {
        setMessage({ text, type });
    };

    const getLeverInsight = (question, score) => {
        const insights = {
            "🌪 Chaos Level?": {
                1: { label: "Zen Mode", emoji: "🌿", description: "Very calm and structured day", intensity: "Low" },
                5: { label: "Managed Chaos", emoji: "🌪️", description: "Balanced but some turbulence", intensity: "Medium" },
                9: { label: "Full Chaos Overload", emoji: "💥", description: "Everything feels out of control", intensity: "Extreme" },
            },
            "🔥 Heat Exposure?": {
                1: { label: "Cool & Collected", emoji: "❄️", description: "Low stress environment", intensity: "Low" },
                5: { label: "Under Pressure", emoji: "🔥", description: "Moderate stress levels", intensity: "Medium" },
                9: { label: "Burning Hot", emoji: "🌋", description: "High risk of burnout", intensity: "Extreme" },
            },
            "🎭 Humor Shield?": {
                1: { label: "Serious Mode", emoji: "🪨", description: "Struggling to find lightness", intensity: "Low" },
                5: { label: "Decent Defense", emoji: "😌", description: "Some humor helping", intensity: "Medium" },
                9: { label: "Unbreakable Humor", emoji: "🤡", description: "Laughing through the storm", intensity: "Strong" },
            },
            "🧊 Numb Mode?": {
                1: { label: "Fully Alive", emoji: "⚡", description: "High emotional energy", intensity: "Low" },
                5: { label: "Partially Numb", emoji: "🥱", description: "Some emotional shutdown", intensity: "Medium" },
                9: { label: "Frozen Roach", emoji: "🧊", description: "Complete emotional detachment", intensity: "Extreme" },
            },
            "⚡ Focus Spark?": {
                1: { label: "Scattered", emoji: "🌫️", description: "Hard to concentrate", intensity: "Low" },
                5: { label: "Decent Flow", emoji: "🔍", description: "Average focus level", intensity: "Medium" },
                9: { label: "Laser Focus", emoji: "🚀", description: "Deep work monster mode", intensity: "God Tier" },
            },
            "📱 Doom Scroll Damage?": {
                1: { label: "Digital Monk", emoji: "🙏", description: "Minimal distraction", intensity: "Low" },
                5: { label: "Moderate Scroll", emoji: "📲", description: "Some addiction creeping in", intensity: "Medium" },
                9: { label: "Doom Scroller", emoji: "🌀", description: "Heavy dopamine damage", intensity: "Critical" },
            },
            "🧠 Mental Recovery Speed?": {
                1: { label: "Slow Reset", emoji: "🐢", description: "Takes long to bounce back", intensity: "Low" },
                5: { label: "Average Recovery", emoji: "🔄", description: "Decent bounce back", intensity: "Medium" },
                9: { label: "Instant Reset", emoji: "⚡", description: "Recovers extremely fast", intensity: "God Tier" },
            },
        };

        return insights[question]?.[score] || {
            label: "Unknown",
            emoji: "❓",
            description: "No insight available",
            intensity: "Medium",
        };
    };

    const getEvolutionMessage = (day) => {
        if (day <= 10) return "You're just getting started. Every log builds your resilience.";
        if (day <= 30) return "Momentum is building. You're adapting faster than before.";
        if (day <= 60) return "Hardened Survivor mode activated. Chaos fears you now.";
        return "Chaos Master Roach unlocked. You're becoming unstoppable.";
    };

    const calculateAverageScore = (history) => {
        if (!history.length) return 0;
        const total = history.reduce((sum, h) => sum + (h.avgScore || 0), 0);
        return total / history.length;
    };

    const getStrongestLevers = (history) => {
        const strengths = ["Focus Spark", "Mental Recovery", "Humor Shield"];
        return strengths.slice(0, 3);
    };

    // Prevents saving answers from one context under a different context
    useEffect(() => {
        setAnswers({});
        setResult(null);
        setOpenHint(null);
        setMessage({ text: "", type: "" });
        setSuccessMsg("");
    }, [pulseContext]);

    // ─── Initial Load ──────────────────────────────────────────────────────────
    // useEffect(() => {
    //     if (!hasFetchedRoachRef.current) {
    //         loadProgress();
    //     }
    // }, []);
    useEffect(() => {
        // Load data when user switches to streak screen (or on initial mount)
        if (screen === "streak" && !hasFetchedRoachRef.current) {
            loadProgress();
        }
    }, [screen]);   // Important: depend on screen

    const loadProgress = async () => {
        if (useSample) {
            setCurrentDay(9);
            setStreak(8);
            setHistory(sampleRoaches);
            setProgressLoading(false);
            hasFetchedRoachRef.current = true;
            return;
        }

        setProgressLoading(true);
        const config = {
            url: "/pd/roach/progress",
            method: "post",
            data: { userId, subscriptionId },
            headers: { Authorization: authState.token },
        };

        const res = await fetchData(config);
        if (res?.status) {
            setCurrentDay(res.currentDay);
            setStreak(res.streak);
            setHistory(res.history || []);
        }
        setProgressLoading(false);
        hasFetchedRoachRef.current = true;
    };

    // ─── Answer Selection ──────────────────────────────────────────────────────
    const selectAnswer = (questionIndex, value) => {
        setAnswers((prev) => ({
            ...prev,
            [questionIndex]: value,
        }));
    };

    // ─── Generate Result ───────────────────────────────────────────────────────
    const generateResult = (answerValues) => {
        const avg = parseFloat(
            (answerValues.reduce((a, b) => a + b, 0) / answerValues.length).toFixed(1)
        );
        let type = "🪳 Balanced Survivor";
        if (avg >= 8) type = "⚡ Apex Chaos Roach";
        else if (avg >= 6) type = "🔥 Hardened Survivalist";
        else if (avg <= 3) type = "🧊 Low Energy Roach";
        setResult({ avg, type });
    };

    const generateProfile = () => {
        const answerValues = Object.values(answers);
        if (answerValues.length !== roachquestions.length) {
            showMessage(
                `Please answer all ${roachquestions.length} questions.`,
                "error"
            );
            return;
        }
        generateResult(answerValues);
    };

    // ─── Validation ────────────────────────────────────────────────────────────
    const validateRoachDate = () => {
        if (!roachDate) {
            showMessage("Please select a date.", "error");
            return false;
        }

        const selectedDate = normalizeDate(roachDate);
        const today = normalizeDate(new Date());
        const minDate = normalizeDate(startDate);
        const maxDate =
            new Date(endDate) < new Date() ? normalizeDate(endDate) : today;

        if (selectedDate > today) {
            showMessage("Future dates are not allowed.", "error");
            return false;
        }
        if (selectedDate < minDate) {
            showMessage(`Date must be on or after ${minDate}.`, "error");
            return false;
        }
        if (selectedDate > maxDate) {
            showMessage(`Date must be on or before ${maxDate}.`, "error");
            return false;
        }

        // BUG 2 FIX: was `selectedSubscriptionId` (undefined). Fixed to `subscriptionId`.
        const alreadyExists = history.some(
            (roach) =>
                normalizeDate(roach.roachDate) === selectedDate &&
                roach.pulseContext === pulseContext &&
                roach.subscriptionId === subscriptionId
        );

        if (alreadyExists) {
            showMessage(
                `🪳 ${pulseContext} Pulse already exists for this date.`,
                "error"
            );
            return false;
        }

        return true;
    };

    // ─── Save ──────────────────────────────────────────────────────────────────
    const saveDay = async () => {
        if (!validateRoachDate()) return;

        if (!result) {
            showMessage("Please answer all questions first.", "error");
            return;
        }
        if (!subscriptionId) {
            showMessage("Subscription ID is missing.", "error");
            return;
        }

        setLoading(true);
        setSuccessMsg("");

        const leversString = Object.values(answers).join(",");

        const payload = {
            subscriptionId,
            user: userId,
            userCode,
            pulseContext,
            levers: leversString,
            dayNumber: currentDay,
            setId: "1",
            roachDate,
        };

        const config = {
            url: "/pd/roach/submit",
            method: "post",
            data: payload,
            headers: { Authorization: authState?.token },
        };

        try {
            const res = await fetchData(config);

            if (res?.status) {
                const newEntry = {
                    ...res.roach,
                    pulseContext: res.roach.pulseContext || pulseContext,
                    subscriptionId: res.roach.subscriptionId || subscriptionId,
                };

                // Update history — deduplicate by date + context + subscription
                setHistory((prev) => {
                    const merged = [newEntry, ...prev];
                    return merged.filter(
                        (item, index, self) =>
                            index ===
                            self.findIndex(
                                (r) =>
                                    normalizeDate(r.roachDate) === normalizeDate(item.roachDate) &&
                                    r.pulseContext === item.pulseContext &&
                                    r.subscriptionId === item.subscriptionId
                            )
                    );
                });

                if (res.roach?.dayNumber) {
                    setCurrentDay(res.roach.dayNumber + 1);
                } else {
                    setCurrentDay((prev) => prev + 1);
                }
                setStreak((prev) => prev + 1);

                setAnswers({});
                setOpenHint(null);
                setResult(null);

                if (!isDuplicateEntry) {
                    setSuccessMsg("🎉 Survival log saved successfully!");
                    showMessage("Survival log saved successfully 🪳", "success");
                }
                setScreen("evolution");
            } else {
                showMessage(res?.msg || res?.message || "Failed to save", "error");
            }
        } catch (err) {
            console.error("Save Error:", err);
            showMessage("Failed to save. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    // ─── Render Helpers ────────────────────────────────────────────────────────
    const getScoreLabel = (score) => {
        if (score === 1) return "Low";
        if (score === 5) return "Medium";
        if (score === 9) return "High";
        return "Unknown";
    };

    const getScoreColor = (score) => {
        if (score === 1) return "bg-green-500/20 text-green-400";
        if (score === 5) return "bg-yellow-500/20 text-yellow-400";
        if (score === 9) return "bg-red-500/20 text-red-400";
        return "bg-gray-500/20 text-gray-200";
    };

    // ─── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="space-y-6 bg-gray-900 p-6 rounded-xl text-white border-t-2 border-purple-400 mt-2">
            <div className="min-h-screen bg-[#0b0f1a] text-white">
                {useSample && (
                    <div className="space-y-6 bg-gray-700 p-6 rounded-xl text-white border-t-2 border-cyan-200 mt-2">
                        📦 You are viewing SAMPLE DASHBOARD DATA (for demo purpose)
                    </div>
                )}

                {/* HEADER */}
                <div className="p-4 bg-[#111827] border-b border-gray-800">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm lg:text-lg text-cyan-200">
                                🪳 90-Day Self BOS Pulse Challenge
                            </div>
                            <div className="text-[10px] lg:text-xs text-gray-200 mt-1">
                                Daily Multi Context Pulse Resilience, Drift & Survival Psychology Tracker
                            </div>
                        </div>
                        <div className="bg-green-500/20 text-green-300 px-3 py-1 rounded-xl text-xs font-bold">
                            Day {currentDay}/90
                        </div>
                    </div>
                </div>

                {/* TAB NAV */}
                <div className="flex flex-wrap gap-1 bg-[#2e3546] border-b border-gray-800 px-4">
                    {[
                        ["today", "🪳 Pulse Feed"],
                        ["evolution", "🧬 Daily Evolution"],
                        ["campus", "🏫 Campus"],
                        ["drift", "🧭 Drift"],
                        ["streak", "🔥 Pulse Streaks"],
                    ].map(([id, label]) => (
                        <button
                            key={id}
                            onClick={() => setScreen(id)}
                            className={`w-fit px-2 py-1 text-gray-100 text-[11px] lg:text-xs rounded-lg font-semibold transition-all border
                            ${screen === id
                                    ? "bg-blue-700 text-white border-blue-400"
                                    : "bg-[#0f172a] text-gray-100 border-gray-800"
                                }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* DATE + CONTEXT PICKER — always visible */}
                <div className="flex flex-col border border-gray-600 px-2 rounded-md mt-2">
                    <div className="flex flex-col">
                        <label className="text-xs lg:text-sm text-gray-100">Select Date</label>
                        <input
                            type="date"
                            value={roachDate}
                            onChange={(e) => setRoachDate(e.target.value)}
                            min={startDate?.split("T")[0]}
                            max={maxAllowedDate}
                            disabled={!isActiveSub}
                            className="bg-gray-700 text-gray-100 w-fit text-xs lg:text-sm px-2 py-1 rounded-lg border border-gray-500 disabled:opacity-60"
                        />
                    </div>
                    <div className="flex flex-col">
                        <label className="text-xs lg:text-sm text-cyan-200">Pick One Pulse Context</label>
                        <div className="flex flex-wrap gap-2 pb-1">
                            {pulseContexts.map((ctx) => (
                                <button
                                    key={ctx.id}
                                    onClick={() => setPulseContext(ctx.id)}
                                    className={`flex-shrink-0 px-2 py-1 rounded-lg text-xs w-fit border transition-all
                                        ${pulseContext === ctx.id
                                            ? "bg-cyan-500 text-black border-cyan-400 font-semibold"
                                            : "bg-[#1f2937] text-gray-300 border-gray-700 hover:bg-[#374151]"
                                        }`}
                                >
                                    {ctx.emoji} {ctx.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* SCREENS */}
                <div className="p-3 pb-28 space-y-4">

                    {/* ── TODAY SCREEN ── */}
                    {screen === "today" && (
                        <div className="space-y-2">
                            <div className="bg-[#0f172a] p-4 rounded-xl border border-gray-800">
                                <div className="flex flex-col">
                                    <div className="flex flex-row">
                                        <h2 className="w-fit px-2 py-1 flex flex-row justify-between gap-2 text-cyan-200 rounded-sm text-xs lg:text-sm">
                                            <span className="text-sm bg-gray-200">🪳</span>
                                            Daily Resilience Evolution Tracker
                                            <span className="ml-2 text-[10px] lg:text-xs bg-cyan-500/20 text-cyan-200 px-2 py-0.5 rounded">
                                                {selectedPulseEmoji} {selectedPulseLabel}
                                            </span>
                                        </h2>
                                    </div>
                                    <div className="flex flex-row items-center justify-between">
                                        <button
                                            onClick={() => {
                                                setShowHistory(!showHistory);
                                                // BUG 3 FIX: only re-fetch if fetch hasn't completed yet
                                                if (!hasFetchedRoachRef.current) {
                                                    loadProgress();
                                                }
                                            }}
                                            className="w-fit px-2 py-1 flex items-center justify-center bg-cyan-700 hover:bg-cyan-400 text-white rounded-sm text-[11px] lg:text-xs"
                                        >
                                            📜 View/Edit History ({selectedPulseLabel})
                                        </button>
                                        <div className="flex flex-row items-baseline gap-1">
                                            <span className="text-2xl font-black text-green-400 leading-none">
                                                {currentDay}
                                            </span>
                                            <span className="text-[10px] text-gray-500 leading-none">
                                                /90 Days
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* HISTORY LIST */}
                                {showHistory && (
                                    <div className="mt-3 space-y-3">
                                        {displayRoaches
                                            .slice()
                                            .sort((a, b) => new Date(b.roachDate) - new Date(a.roachDate))
                                            .map((item, index) => {
                                                const scores = item.levers
                                                    ? item.levers.split(",").map(Number)
                                                    : [];

                                                return (
                                                    <div
                                                        key={item._id}
                                                        className="bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 border border-slate-700 rounded-2xl overflow-hidden shadow-lg"
                                                    >
                                                        <button
                                                            onClick={() =>
                                                                setSelectedRoachId(
                                                                    selectedRoachId === item._id
                                                                        ? null
                                                                        : item._id
                                                                )
                                                            }
                                                            className="w-full text-left p-3 lg:p-4"
                                                        >
                                                            <div className="flex items-start justify-between gap-3">
                                                                <div className="min-w-0 flex-1">
                                                                    <div className="flex flex-wrap items-center gap-2">
                                                                        <span className="px-2 py-1 rounded-full bg-cyan-500/15 text-cyan-300 text-xs font-bold">
                                                                            #{index + 1}
                                                                        </span>
                                                                        <span className="px-2 py-1 rounded-full bg-purple-500/15 text-purple-300 text-xs font-bold">
                                                                            Day {item.dayNumber}
                                                                        </span>
                                                                        <span className="px-2 py-1 rounded-full bg-slate-700 text-gray-100 text-xs">
                                                                            {new Date(item.roachDate).toLocaleDateString("en-IN")}
                                                                        </span>
                                                                    </div>
                                                                    <div className="mt-3 text-sm lg:text-base font-bold text-white">
                                                                        🪳 {item.type || "Roach Assessment"}
                                                                    </div>
                                                                    <div className="mt-1 text-xs lg:text-sm text-gray-200">
                                                                        Set ID: {item.setId}
                                                                    </div>
                                                                </div>
                                                                <div className="text-right flex-shrink-0">
                                                                    <div className="text-[10px] uppercase tracking-wide text-gray-500">
                                                                        Avg
                                                                    </div>
                                                                    <div
                                                                        className={`text-xl lg:text-2xl font-black ${
                                                                            item.avgScore >= 8
                                                                                ? "text-green-300"
                                                                                : item.avgScore >= 5
                                                                                ? "text-yellow-300"
                                                                                : "text-red-300"
                                                                        }`}
                                                                    >
                                                                        {item.avgScore?.toFixed(1)}
                                                                    </div>
                                                                    <div className="text-gray-500 text-xs">
                                                                        Score
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="mt-4 flex items-center justify-between">
                                                                <div className="text-xs text-gray-200">
                                                                    Tap to view full assessment
                                                                </div>
                                                                <div className="text-cyan-300 text-xs font-semibold">
                                                                    {selectedRoachId === item._id ? "▲ Hide" : "▼ View"}
                                                                </div>
                                                            </div>
                                                        </button>

                                                        {selectedRoachId === item._id && (
                                                            <div className="border-t border-slate-700 p-3 lg:p-4">
                                                                <div className="mb-4 flex items-center justify-between">
                                                                    <div className="text-cyan-300 font-bold text-xs lg:text-sm">
                                                                        🧠 Lever Breakdown
                                                                    </div>
                                                                    <div className="text-xs text-gray-200">
                                                                        {scores.length} Levers
                                                                    </div>
                                                                </div>
                                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                                                    {roachquestions.map((question, idx) => {
                                                                        const score = scores[idx] || 0;
                                                                        const insight = getLeverInsight(question, score);
                                                                        return (
                                                                            <div
                                                                                key={idx}
                                                                                className="bg-slate-800/60 border border-slate-700 rounded-xl p-3"
                                                                            >
                                                                                <div className="flex justify-between gap-3">
                                                                                    <div className="flex-1">
                                                                                        <div className="text-xs lg:text-sm font-semibold text-white">
                                                                                            {question}
                                                                                        </div>
                                                                                        <div className="mt-1 text-cyan-300 text-xs lg:text-sm font-semibold">
                                                                                            {insight?.label}
                                                                                        </div>
                                                                                        <div className="mt-2 text-xs lg:text-sm text-gray-100 leading-relaxed">
                                                                                            {insight?.description}
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="text-right">
                                                                                        <div className="text-lg">{insight?.emoji}</div>
                                                                                        <div
                                                                                            className={`font-black text-lg ${
                                                                                                score >= 8
                                                                                                    ? "text-green-300"
                                                                                                    : score >= 5
                                                                                                    ? "text-yellow-300"
                                                                                                    : "text-red-300"
                                                                                            }`}
                                                                                        >
                                                                                            {score}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                {roachHints[idx] && (
                                                                                    <div className="mt-3">
                                                                                        <button
                                                                                            onClick={() =>
                                                                                                setExpandedHints((prev) => ({
                                                                                                    ...prev,
                                                                                                    [idx]: !prev[idx],
                                                                                                }))
                                                                                            }
                                                                                            className="w-full flex items-center justify-between bg-slate-900 hover:bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 transition-all"
                                                                                        >
                                                                                            <span className="text-cyan-300 text-xs font-semibold">
                                                                                                Lever Insights
                                                                                            </span>
                                                                                            <span className="text-lg">
                                                                                                {expandedHints[idx] ? "▲" : "▼"}
                                                                                            </span>
                                                                                        </button>

                                                                                        {expandedHints[idx] && (
                                                                                            <div className="mt-2 space-y-2">
                                                                                                <div className="bg-slate-900 rounded-lg p-2">
                                                                                                    <div className="text-purple-300 text-[10px] uppercase font-bold">
                                                                                                        Meaning
                                                                                                    </div>
                                                                                                    <div className="text-gray-100 text-xs mt-1">
                                                                                                        {roachHints[idx].meaning}
                                                                                                    </div>
                                                                                                </div>
                                                                                                <div className="bg-slate-900 rounded-lg p-2">
                                                                                                    <div className="text-red-300 text-[10px] uppercase font-bold">
                                                                                                        Impact
                                                                                                    </div>
                                                                                                    <div className="text-gray-100 text-xs mt-1">
                                                                                                        {roachHints[idx].impact}
                                                                                                    </div>
                                                                                                </div>
                                                                                                <div className="bg-slate-900 rounded-lg p-2">
                                                                                                    <div className="text-green-300 text-[10px] uppercase font-bold">
                                                                                                        Improve
                                                                                                    </div>
                                                                                                    <div className="text-gray-100 text-xs mt-1">
                                                                                                        {roachHints[idx].improve}
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>

                                                                <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-2">
                                                                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-3 text-center">
                                                                        <div className="text-gray-200 text-[10px] uppercase">Avg Score</div>
                                                                        <div className="mt-1 text-purple-300 font-black text-lg">
                                                                            {item.avgScore?.toFixed(1)}
                                                                        </div>
                                                                    </div>
                                                                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-3 text-center">
                                                                        <div className="text-gray-200 text-[10px] uppercase">Day</div>
                                                                        <div className="mt-1 text-cyan-300 font-black text-lg">
                                                                            {item.dayNumber}
                                                                        </div>
                                                                    </div>
                                                                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-3 text-center">
                                                                        <div className="text-gray-200 text-[10px] uppercase">Set</div>
                                                                        <div className="mt-1 text-yellow-300 font-black text-lg">
                                                                            {item.setId}
                                                                        </div>
                                                                    </div>
                                                                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-3 text-center">
                                                                        <div className="text-gray-200 text-[10px] uppercase">Status</div>
                                                                        <div className="mt-1 text-green-300 font-black text-sm">
                                                                            ACTIVE
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}

                                <div className="mt-4">
                                    <Bar value={(currentDay / 90) * 100} color="bg-green-400" />
                                </div>
                            </div>

                            {/* ── QUESTION CARDS ── */}
                            {roachquestions.map((question, index) => (
                                <div
                                    key={index}
                                    className="mb-4 p-4 border border-gray-700 rounded-xl"
                                >
                                    <div className="flex flex-col">
                                        <div className="flex text-end items-end justify-end gap-2 flex-shrink-0">
                                            <button
                                                onClick={() => setOpenHint(openHint === index ? null : index)}
                                                className="h-8 w-8 flex items-center justify-center rounded-lg border border-gray-700 hover:border-cyan-400 transition"
                                            >
                                                ℹ️
                                            </button>

                                            <button
                                                onClick={() => toggleGenAIHelp(index)}
                                                className={`w-fit h-8 px-3 flex items-center justify-center rounded-lg text-[10px] font-bold transition-all whitespace-nowrap ${
                                                    openGenAI === index
                                                        ? "bg-purple-500 text-white"
                                                        : "bg-[#111827] text-purple-300 border border-purple-500/30"
                                                }`}
                                            >
                                                {openGenAI === index ? "🙈 Hide GenAI" : "🤖 Ask GenAI"}
                                            </button>
                                        </div>
                                    </div>

                                    {openHint === index && roachHints[index] && (
                                        <div className="mt-1 bg-[#111827] p-3 rounded-lg">
                                            <div className="text-cyan-300 text-xs">
                                                {roachHints[index].meaning}
                                            </div>
                                        </div>
                                    )}

                                    {openGenAI === index && roachHints[index] && (
                                        <div className="w-full bg-[#111827] rounded-2xl border border-purple-500/20 p-2">
                                            <AILightComponent
                                                    mode="daily_pulse_mood_resilience"
                                                    param1={"The user is answering a Daily Pulse assessment."}
                                                    param2={`
                                                        Question:${question}

                                                        Question Meaning:${roachHints[index]?.meaning || "Not Available"}

                                                        Assessment Context:${pulseContext}
                                                `}
                                                />
                                        </div>
                                    )}

                                    <div className="text-xs lg:text-sm font-bold text-yellow-200">
                                        {selectedPulseEmoji} Q{index + 1}. {question}
                                    </div>

                                    <div className="flex flex-row gap-2 mt-1">
                                        {[1, 5, 9].map((v) => (
                                            <button
                                                key={v}
                                                onClick={() => selectAnswer(index, v)}
                                                className={`w-fit text-[10px] lg:text-xs px-2 py-1 rounded-lg border ${
                                                    answers[index] === v
                                                        ? "border-emerald-400 bg-emerald-600 text-white font-semibold"
                                                        : "border-gray-700"
                                                }`}
                                            >
                                                {v === 1 ? "🟢 Low" : v === 5 ? "🟡 Medium" : "🔴 High"}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}

                            {/* GENERATE BUTTON */}
                            <button
                                onClick={generateProfile}
                                disabled={Object.keys(answers).length !== roachquestions.length}
                                className="mt-4 bg-cyan-500 px-4 py-2 rounded-xl text-black font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Generate Survival Profile
                            </button>

                            {/* RESULT CARD */}
                            {result && (
                                <div className="bg-[#0f172a] p-5 rounded-xl border border-gray-800 mt-4">
                                    <div className="text-xs text-gray-200 uppercase tracking-wide">
                                        Survival Identity
                                    </div>
                                    <div className="text-2xl font-black mt-2">{result.type}</div>

                                    <div className="mt-4">
                                        <div className="flex justify-between text-xs mb-2">
                                            <span className="text-gray-200">Survival Score</span>
                                            <span className="text-cyan-300 font-bold">
                                                {result.avg.toFixed(2)} / 9
                                            </span>
                                        </div>
                                        <Bar value={(result.avg / 9) * 100} color="bg-cyan-400" />
                                    </div>

                                    {successMsg && !isDuplicateEntry && (
                                        <div
                                            className={`mt-4 p-3 rounded-xl text-center text-sm font-medium ${
                                                successMsg.includes("Failed")
                                                    ? "bg-red-500/20 text-red-400"
                                                    : "bg-green-500/20 text-green-400"
                                            }`}
                                        >
                                            {successMsg}
                                        </div>
                                    )}

                                    {message.text && (
                                        <div
                                            className={`mt-4 p-3 rounded-xl text-center text-sm font-medium ${
                                                message.type === "error"
                                                    ? "bg-red-500/20 text-red-400"
                                                    : "bg-green-500/20 text-green-400"
                                            }`}
                                        >
                                            {message.text}
                                        </div>
                                    )}

                                    {!isActiveSub && (
                                        <div className="mt-4 p-3 rounded-xl text-center text-sm bg-red-500/20 text-red-400">
                                            Subscription is inactive
                                        </div>
                                    )}

                                    {isActiveSub && roachDate && new Date(roachDate) > new Date(endDate) && (
                                        <div className="mt-4 p-3 rounded-xl text-center text-sm bg-red-500/20 text-red-400">
                                            Selected date is outside subscription range
                                        </div>
                                    )}

                                    {/* BUG 2 FIX: duplicate check uses `subscriptionId` not `selectedSubscriptionId` */}
                                    {isDuplicateEntry && (
                                        <div className="mt-4 p-3 rounded-xl text-center text-sm bg-yellow-500/20 text-yellow-400">
                                            🪳 {pulseContext} Pulse already saved for this date.
                                        </div>
                                    )}

                                    <button
                                        onClick={saveDay}
                                        disabled={isSaveDisabled}
                                        className="w-fit mt-6 bg-green-500 hover:bg-green-400 text-black px-4 py-2 rounded-xl font-bold disabled:opacity-70 disabled:cursor-not-allowed"
                                    >
                                        {loading ? "Saving Survival Log..." : "🧬 Save & Evolve"}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── EVOLUTION SCREEN ── */}
                    {screen === "evolution" && (
                        <PulseEvolutionScreen pulseContext={pulseContext} />
                    )}

                    {/* ── STREAK SCREEN ── */}
                    {screen === "streak" && (
                        <div className="space-y-2 px-2 pb-4 border-t border-orange-400 rounded-xl py-2">
                            {history.length === 0 && (
                                <div className="text-center py-6">
                                    <button
                                    onClick={loadProgress}
                                    className="bg-violet-600 hover:bg-violet-500 px-6 py-2 rounded-xl text-sm"
                                    >
                                    Load Pulse History
                                    </button>
                                </div>
                            )}
                            <PulseStreakCalendar pulseContext={pulseContext} />
                        </div>
                    )}

                    {/* ── Campus ── */}
                    {screen === "campus" && (
                        <CampusPulse pulseContext={"Campus"} campusId={"C0000000"} />
                    )}                

                    {/* ── Drift ── */}
                    {screen === "drift" && (
                        <DriftIntelligence pulseContext={pulseContext} />
                    )}                

                    {/* ── FEED SCREEN ── */}
                    {screen === "feed" && (
                        <div className="space-y-3">
                            <div className="bg-[#0f172a] p-4 rounded-xl border border-gray-800">
                                <div className="text-lg font-bold">👥 Survival Feed</div>
                                <div className="text-xs text-gray-200 mt-1">Your latest resilience logs</div>
                            </div>

                            {displayRoaches.length === 0 && (
                                <div className="text-xs text-gray-500 text-center py-10">
                                    No survival logs yet. Start today!
                                </div>
                            )}

                            {displayRoaches.map((h, i) => {
                                const scores = h.levers
                                    ? h.levers.split(",").map(Number)
                                    : h.score || [];

                                return (
                                    <div key={i} className="bg-[#111827] p-4 rounded-xl border border-gray-800">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="font-bold">🪳 Day {h.dayNumber || h.day}</div>
                                                <div className="text-[11px] text-gray-200 mt-1">
                                                    {h.type || "Survival Entry"}
                                                </div>
                                            </div>
                                            {h.avgScore && (
                                                <div className="text-right">
                                                    <div className="text-cyan-400 font-bold text-lg">
                                                        {h.avgScore.toFixed(2)}
                                                    </div>
                                                    <div className="text-[10px] text-gray-100">Avg Score</div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="mt-4 space-y-3">
                                            {scores.map((score, idx) => {
                                                const lever = roachquestions[idx];
                                                const { label, emoji, description, intensity } =
                                                    getLeverInsight(lever, score);
                                                return (
                                                    <div
                                                        key={idx}
                                                        className="bg-[#0f172a] border border-gray-800 rounded-xl p-4 hover:border-gray-700 transition-all"
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <div
                                                                className={`w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center text-xl font-black
                                                                ${score === 9
                                                                    ? "bg-red-500/20 text-red-400"
                                                                    : score === 5
                                                                    ? "bg-yellow-500/20 text-yellow-400"
                                                                    : "bg-green-500/20 text-green-400"
                                                                }`}
                                                            >
                                                                {score}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-lg">{lever}</span>
                                                                    <span className="text-xl">{emoji}</span>
                                                                </div>
                                                                <div className="mt-1 text-sm font-semibold text-gray-200">
                                                                    {label}
                                                                </div>
                                                                <div className="text-xs text-gray-200 mt-1 leading-snug">
                                                                    {description}
                                                                </div>
                                                                <div className="mt-3">
                                                                    <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                                                                        <span>Intensity</span>
                                                                        <span>{intensity}</span>
                                                                    </div>
                                                                    <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                                                                        <div
                                                                            className={`h-full rounded-full transition-all ${
                                                                                score === 9
                                                                                    ? "bg-red-500"
                                                                                    : score === 5
                                                                                    ? "bg-yellow-500"
                                                                                    : "bg-green-500"
                                                                            }`}
                                                                            style={{ width: `${(score / 9) * 100}%` }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PDRoach;
